import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Stack,
  Card,
  CardContent,
  Chip,
  Switch,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTextField, FormSelect } from '@personal-dashboard/ui';
import { LocalField } from './customFieldsQueries';

interface CustomFieldCardProps {
  sectionId: string;
  field: LocalField;
  onUpdateField: (sectionId: string, fieldId: string, updates: Partial<LocalField>) => void;
  onDeleteField: (sectionId: string, fieldId: string) => void;
  onAddOption: (sectionId: string, fieldId: string, optionValue: string) => void;
  onRemoveOption: (sectionId: string, fieldId: string, optionToRemove: string) => void;
  onTogglePublish: (sectionId: string, fieldId: string, published: boolean) => void;
}

interface FieldFormValues {
  name: string;
  type: string;
}

export const CustomFieldCard: React.FC<CustomFieldCardProps> = ({
  sectionId,
  field,
  onUpdateField,
  onDeleteField,
  onAddOption,
  onRemoveOption,
  onTogglePublish,
}) => {
  const isTempField = field.id.startsWith('field_');
  const isPublished = field.published !== false;

  const fieldMethods = useForm<FieldFormValues>({
    defaultValues: { name: field.name, type: field.type },
  });
  const watchedName = fieldMethods.watch('name');
  const watchedType = fieldMethods.watch('type');

  useEffect(() => {
    if (watchedName !== field.name) {
      onUpdateField(sectionId, field.id, { name: watchedName });
    }
  }, [watchedName, sectionId, field.id, field.name, onUpdateField]);

  useEffect(() => {
    if (watchedType !== field.type) {
      onUpdateField(sectionId, field.id, { type: watchedType });
    }
  }, [watchedType, sectionId, field.id, field.type, onUpdateField]);

  useEffect(() => {
    const currentName = fieldMethods.getValues('name');
    const currentType = fieldMethods.getValues('type');
    if (currentName !== field.name || currentType !== field.type) {
      fieldMethods.reset({ name: field.name, type: field.type });
    }
  }, [field.name, field.type, fieldMethods]);

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.currentTarget;
      onAddOption(sectionId, field.id, input.value);
      input.value = '';
    }
  };

  const handleAddOptionClick = () => {
    const input = document.getElementById(`new-option-${field.id}`) as HTMLInputElement;
    if (input) {
      onAddOption(sectionId, field.id, input.value);
      input.value = '';
    }
  };

  return (
    <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <FormProvider {...fieldMethods}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormTextField
                name="name"
                label="Field Name"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormSelect
                name="type"
                label="Type"
                options={[
                  { value: 'TEXT', label: 'TEXT' },
                  { value: 'NUMBER', label: 'NUMBER' },
                  { value: 'SELECT', label: 'SELECT' },
                  { value: 'BOOLEAN', label: 'BOOLEAN' },
                  { value: 'CLOCK', label: 'CLOCK' },
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                {isTempField ? (
                  <IconButton
                    onClick={() => onDeleteField(sectionId, field.id)}
                    color="error"
                    size="small"
                    aria-label="Remove field"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <>
                    <Tooltip
                      title={
                        isPublished
                          ? `Published — visible in Day View & widget options`
                          : `Hidden — not shown in Day View or widget options`
                      }
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {isPublished ? 'Published' : 'Hidden'}
                        </Typography>
                        <Switch
                          size="small"
                          checked={isPublished}
                          onChange={(e) => onTogglePublish(sectionId, field.id, e.target.checked)}
                          inputProps={{ 'aria-label': 'Toggle publish status' }}
                        />
                      </Stack>
                    </Tooltip>
                    <Tooltip title="Delete field">
                      <IconButton
                        onClick={() => onDeleteField(sectionId, field.id)}
                        color="error"
                        size="small"
                        aria-label="Delete field"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </FormProvider>

        {field.type === 'SELECT' && (
          <Box sx={{ mt: 2, ml: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Select Options:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <FormProvider {...fieldMethods}>
                <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                  <FormTextField
                    name="newOption"
                    size="small"
                    placeholder="Add Option (e.g. 1 - Poor)"
                    id={`new-option-${field.id}`}
                    onKeyDown={handleOptionKeyDown}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAddOptionClick}
                  >
                    Add Option
                  </Button>
                </Box>
              </FormProvider>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {field.options.map((opt) => (
                <Chip
                  key={opt}
                  label={opt}
                  onDelete={() => onRemoveOption(sectionId, field.id, opt)}
                  size="small"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
